import React, { useState } from 'react';
import Button from '../components/Button';
import PortalCard from '../components/portal/PortalCard';
import { useAuth } from '../context/AuthContext';
import { classifyError } from '../domain/authErrors';
import { familyPortalWord as w } from '../domain/familyPortal';
import s from './PortalPage.module.css';

// /portal/signin (#771, #772): where a family signs in again.
//
// The teacher's sign-in page (/login) offers a free trial, and its Google
// button makes a new account — a new studio — for an address Firebase has not
// seen. Neither belongs in front of a family (#764). So this is the Family
// Portal's own card: the email address and password the
// family chose on the invitation's join page (#770), and "Forgot your
// password?", which is Firebase's own reset email — the one /login sends.
// No Google, because nothing here may create a login: a family's login is
// only ever made by an invitation (functions/portal/invites.js).
//
// MMS's families sign in on an unbranded page with the same two fields and
// the same link (#543 §2, help article 1080).
//
// Signing in makes the user, and PortalApp then sends this path on to
// /portal; nothing here navigates.
export default function PortalSignInPage() {
  const { login, resetPassword } = useAuth();
  const [mode, setMode] = useState('signIn');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [sent, setSent] = useState(false);

  const signIn = (e) => {
    e.preventDefault();
    if (!email || !password) return;
    setBusy(true);
    setError(null);
    login(email.trim(), password).then(
      () => setBusy(false),
      (err) => {
        setBusy(false);
        setError(signInRefusal(err));
      },
    );
  };

  const reset = (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    setError(null);
    // The same sentence whether or not the address has a login: a reset form
    // that said "no such login" would tell anybody which addresses do
    // (components/auth/ResetPasswordPanel.jsx has the argument).
    resetPassword(email.trim()).then(
      () => { setBusy(false); setSent(true); },
      (err) => {
        setBusy(false);
        if (classifyError(err).kind === 'banner' && err?.code === 'auth/network-request-failed') {
          setError(w('signInOffline'));
        } else {
          setSent(true);
        }
      },
    );
  };

  const to = (next) => { setMode(next); setError(null); setSent(false); };

  const emailField = (
    <label className={s.label}>
      {w('signInEmail')}
      <input
        className={s.input}
        type="email"
        autoComplete="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        required
      />
    </label>
  );

  if (mode === 'reset') {
    return (
      <PortalCard title={w('resetTitle')}>
        {sent ? (
          <>
            <p className={s.body}>{w('joinResetSent')}</p>
            <div className={s.form}>
              <button type="button" className={s.linkButton} onClick={() => to('signIn')}>{w('resetBack')}</button>
            </div>
          </>
        ) : (
          <>
            <p className={s.body}>{w('resetLead')}</p>
            <form className={s.form} onSubmit={reset}>
              {emailField}
              {error ? <p className={s.error}>{error}</p> : null}
              <Button type="submit" loading={busy} disabled={!email}>{w('resetSubmit')}</Button>
              <button type="button" className={s.linkButton} onClick={() => to('signIn')}>{w('resetBack')}</button>
            </form>
          </>
        )}
      </PortalCard>
    );
  }

  return (
    <PortalCard title={w('signInTitle')}>
      <p className={s.body}>{w('signInLead')}</p>
      <form className={s.form} onSubmit={signIn}>
        {emailField}
        <label className={s.label}>
          {w('signInPassword')}
          <input
            className={s.input}
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </label>
        {error ? <p className={s.error}>{error}</p> : null}
        <Button type="submit" loading={busy} disabled={!email || !password}>{w('signInSubmit')}</Button>
        <button type="button" className={s.linkButton} onClick={() => to('reset')}>{w('signInForgot')}</button>
      </form>
      <p className={s.hint}>{w('signInNoLogin')}</p>
    </PortalCard>
  );
}

// Firebase's refusal as one of the portal's sentences. Wrong address and
// wrong password are one answer, as Firebase makes them one.
function signInRefusal(err) {
  const { kind } = classifyError(err);
  if (kind === 'tooMany') return w('signInTooMany');
  if (kind === 'banner' && err?.code === 'auth/network-request-failed') return w('signInOffline');
  return w('signInFailed');
}
